"use client";

import Link from "next/link";
import { NAVIGATION_REGISTRY } from "@/app/lib/search/navigationRegistry";
import { SearchIcon } from "@/app/Components/UniversalSearch/searchIcons";
import {
  MapPinIcon,
  TruckIcon,
  BuildingOffice2Icon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  BuildingStorefrontIcon,
  Squares2X2Icon,
  BookOpenIcon,
  BoltIcon,
  CalendarDaysIcon,
  EyeSlashIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";

export type ShortcutWidgetId =
  | "scheduling"
  | "degree-planning"
  | "dining"
  | "course-sniper"
  | "events"
  | "parking"
  | "bus"
  | "room"
  | "prereqs"
  | "explore"
  | "assistant"
  | "research"
  | "info-rutgers";

export const SHORTCUT_WIDGETS: Array<{
  id: ShortcutWidgetId;
  title: string;
  description: string;
  href: string;
  cta: string;
  icon: React.ComponentType<{ className?: string }>;
  accent:
    | "amber"
    | "blue"
    | "indigo"
    | "emerald"
    | "violet"
    | "rose"
    | "sky"
    | "orange"
    | "cyan"
    | "fuchsia"
    | "teal"
    | "purple";
}> = [
  {
    id: "scheduling",
    title: "Schedule Builder",
    description: "Build and optimize your class schedules",
    href: "/scheduling",
    cta: "Open Schedule Builder",
    icon: Squares2X2Icon,
    accent: "cyan",
  },
  {
    id: "degree-planning",
    title: "Degree Planning",
    description: "Track requirements and plan semesters",
    href: "/degree-planning",
    cta: "Open Degree Planning",
    icon: BookOpenIcon,
    accent: "fuchsia",
  },
  {
    id: "dining",
    title: "Dining",
    description: "Menus, hours, and campus dining locations",
    href: "/dining",
    cta: "Open Dining",
    icon: BuildingStorefrontIcon,
    accent: "orange",
  },
  {
    id: "course-sniper",
    title: "Course Sniper",
    description: "Get notified when a section opens",
    href: "/scheduling/alerts",
    cta: "Open Course Sniper",
    icon: BoltIcon,
    accent: "teal",
  },
  {
    id: "events",
    title: "Campus Events",
    description: "What's happening on campus",
    href: "/events",
    cta: "Browse Events",
    icon: CalendarDaysIcon,
    accent: "purple",
  },
  {
    id: "parking",
    title: "Parking",
    description: "Find lots and availability",
    href: "/parking",
    cta: "Open Parking",
    icon: MapPinIcon,
    accent: "amber",
  },
  {
    id: "bus",
    title: "Bus Tracker",
    description: "Live bus times and routes",
    href: "/bus-tracker",
    cta: "Open Bus Tracker",
    icon: TruckIcon,
    accent: "blue",
  },
  {
    id: "room",
    title: "Room Availability",
    description: "See when rooms are free",
    href: "/room-availability",
    cta: "Open Room Availability",
    icon: BuildingOffice2Icon,
    accent: "indigo",
  },
  {
    id: "prereqs",
    title: "Prerequisites",
    description: "Check if you can take a course",
    href: "/prerequisite-checker",
    cta: "Check Prerequisites",
    icon: ClipboardDocumentCheckIcon,
    accent: "emerald",
  },
  {
    id: "explore",
    title: "Explore Courses",
    description: "Browse and search courses",
    href: "/explore",
    cta: "Explore Courses",
    icon: MagnifyingGlassIcon,
    accent: "violet",
  },
  {
    id: "assistant",
    title: "Ask Scarlet Sync",
    description: "Get help with schedules and planning",
    href: "/ai-assistant",
    cta: "Open Assistant",
    icon: ChatBubbleLeftRightIcon,
    accent: "rose",
  },
  {
    id: "research",
    title: "Research Opportunities",
    description: "Find undergraduate research and REU opportunities",
    href: "/research-opportunities",
    cta: "Find Research",
    icon: AcademicCapIcon,
    accent: "sky",
  },
  {
    id: "info-rutgers",
    title: "Info Rutgers",
    description: "Get answers to all your Rutgers-related questions",
    href: "/info-rutgers",
    cta: "Open Info Rutgers",
    icon: InformationCircleIcon,
    accent: "rose",
  },
];

export const DEFAULT_SHORTCUT_ORDER: ShortcutWidgetId[] = SHORTCUT_WIDGETS.map(({ id }) => id);

const colorStyles: Record<string, { text: string; iconBg: string }> = {
  amber: { text: "text-amber-600 dark:text-amber-400", iconBg: "bg-amber-100 dark:bg-amber-500/10" },
  blue: { text: "text-blue-600 dark:text-blue-400", iconBg: "bg-blue-100 dark:bg-blue-500/10" },
  indigo: { text: "text-indigo-600 dark:text-indigo-400", iconBg: "bg-indigo-100 dark:bg-indigo-500/10" },
  emerald: { text: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-100 dark:bg-emerald-500/10" },
  violet: { text: "text-violet-600 dark:text-violet-400", iconBg: "bg-violet-100 dark:bg-violet-500/10" },
  rose: { text: "text-rose-600 dark:text-rose-400", iconBg: "bg-rose-100 dark:bg-rose-500/10" },
  sky: { text: "text-sky-600 dark:text-sky-400", iconBg: "bg-sky-100 dark:bg-sky-500/10" },
  orange: { text: "text-orange-600 dark:text-orange-400", iconBg: "bg-orange-100 dark:bg-orange-500/10" },
  cyan: { text: "text-cyan-600 dark:text-cyan-400", iconBg: "bg-cyan-100 dark:bg-cyan-500/10" },
  fuchsia: { text: "text-fuchsia-600 dark:text-fuchsia-400", iconBg: "bg-fuchsia-100 dark:bg-fuchsia-500/10" },
  teal: { text: "text-teal-600 dark:text-teal-400", iconBg: "bg-teal-100 dark:bg-teal-500/10" },
  purple: { text: "text-purple-600 dark:text-purple-400", iconBg: "bg-purple-100 dark:bg-purple-500/10" },
};

const shortcutById = Object.fromEntries(SHORTCUT_WIDGETS.map((widget) => [widget.id, widget])) as Record<
  ShortcutWidgetId,
  (typeof SHORTCUT_WIDGETS)[number]
>;

export function sanitizeShortcutOrder(order: ShortcutWidgetId[], hidden: ShortcutWidgetId[]) {
  const validOrder = order.filter(
    (id, index) => SHORTCUT_WIDGETS.some((widget) => widget.id === id) && order.indexOf(id) === index
  );
  const missing = DEFAULT_SHORTCUT_ORDER.filter((id) => !validOrder.includes(id));
  const nextOrder = [...validOrder, ...missing];
  const nextHidden = hidden.filter(
    (id, index) => SHORTCUT_WIDGETS.some((widget) => widget.id === id) && hidden.indexOf(id) === index
  );
  return { order: nextOrder, hidden: nextHidden };
}

export default function DashboardQuickActionWidgets({
  order = DEFAULT_SHORTCUT_ORDER,
  hidden = [],
  customizeMode = false,
  onHideShortcut,
  onDragShortcutStart,
  onDragShortcutOver,
  onDropShortcut,
  draggedShortcutId = null,
}: {
  order?: ShortcutWidgetId[];
  hidden?: ShortcutWidgetId[];
  customizeMode?: boolean;
  onHideShortcut?: (id: ShortcutWidgetId) => void;
  onDragShortcutStart?: (id: ShortcutWidgetId) => void;
  onDragShortcutOver?: (event: React.DragEvent) => void;
  onDropShortcut?: (id: ShortcutWidgetId) => void;
  draggedShortcutId?: ShortcutWidgetId | null;
}) {
  const hiddenSet = new Set(hidden);
  const visibleWidgets = order.filter((id) => !hiddenSet.has(id)).map((id) => shortcutById[id]).filter(Boolean);

  return (
    <div className="grid grid-cols-2 gap-px border border-[var(--ss-border)] bg-[var(--ss-border)] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {visibleWidgets.map(({ id, title, description, href, cta, icon: Icon, accent }) => {
        const style = colorStyles[accent] || colorStyles.blue;
        const cardBody = (
          <>
            <div className={`shrink-0 rounded-control p-2 ${style.iconBg} ${style.text}`}>
              <SearchIcon iconId={NAVIGATION_REGISTRY.find((entry) => entry.href === (href === "/parking" ? "/parking-map" : href))?.iconName} className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-bold text-[var(--ss-text)]">{title}</h3>
              <p className="mt-0.5 line-clamp-1 text-xs text-[var(--ss-text-muted)]">
                {description}
              </p>
              {!customizeMode && (
                <ArrowRightIcon className={`mt-2 h-4 w-4 transition-transform group-hover:translate-x-1 ${style.text}`} aria-hidden />
              )}
            </div>
          </>
        );

        if (customizeMode) {
          return (
            <div
              key={id}
              draggable
              onDragStart={() => onDragShortcutStart?.(id)}
              onDragOver={onDragShortcutOver}
              onDrop={() => onDropShortcut?.(id)}
              className={`relative min-h-24 bg-[var(--ss-surface)] p-3 ${
                draggedShortcutId === id ? "opacity-50 ring-2 ring-red-400" : ""
              } ${customizeMode ? "cursor-grab active:cursor-grabbing" : ""}`}
            >
              <div className="absolute right-3 top-3 flex items-center gap-1">
                <span className="rounded-control border border-[var(--ss-border)] p-1.5 text-[var(--ss-text-muted)]">
                  <Bars3Icon className="h-3.5 w-3.5" aria-hidden />
                </span>
                <button
                  type="button"
                  onClick={() => onHideShortcut?.(id)}
                  className="flex min-h-12 min-w-12 items-center justify-center rounded-control border border-[var(--ss-border)] p-1.5 text-[var(--ss-text-muted)] transition hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] md:min-h-0 md:min-w-0"
                  aria-label={`Hide ${title}`}
                >
                  <EyeSlashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="group flex items-start gap-3 pr-16">{cardBody}</div>
            </div>
          );
        }

        return (
          <Link
            key={id}
            href={href}
            aria-label={cta}
            className="group flex min-h-24 items-start gap-3 bg-[var(--ss-surface)] p-3 transition-colors hover:bg-[var(--ss-surface-subtle)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
          >
            {cardBody}
          </Link>
        );
      })}
    </div>
  );
}
